import React, { FC, useEffect } from 'react'
import { useLoginForm } from '../hooks/useLoginForm'
import { useSsoDetect } from '../hooks/useSsoDetect'
import { LoginFormErrors, LoginPageProps } from '../types'
import { SsoIndicator } from './SsoIndicator'
import { CloudflareTurnstile } from '../../common/CloudflareTurnstile'
import UUIInput from '@aytm/lib/uui/components/UUIInput'
import UUICheckbox from '@aytm/lib/uui/components/UUICheckbox'
import UUIButton from '@aytm/lib/uui/components/UUIButton'
import { GLASS_FIELDS } from '@aytm/app/users/common/glassSurface'
import { trimOnBlur } from '@aytm/app/users/common/trimOnBlur'
import { GLASS_BTN_PRIMARY } from '@aytm/app/users/common/glassSurface'
import { GLASS_BTN_GHOST } from '@aytm/app/users/common/glassSurface'

export const LoginForm: FC<LoginPageProps> = ({
  sessionPath,
  restorePasswordPath,
  registerPath,
  detectProviderPath,
  inboxExpired,
  notice,
  prefilledEmail,
  displayCaptcha,
  turnstileSiteKey,
  allowedRedirectHosts
}) => {
  const { form, setField, setTurnstileToken, errors, loading, handleSubmit, captcha, turnstileResetSignal } =
    useLoginForm({
      sessionPath,
      displayCaptcha,
      turnstileSiteKey,
      allowedRedirectHosts
    })
  const sso = useSsoDetect(form.email, detectProviderPath)
  const showPassword = !sso
  const [passwordVisible, setPasswordVisible] = React.useState(false)
  const emailRef = React.useRef<HTMLInputElement>(null)
  const passwordRef = React.useRef<HTMLInputElement>(null)

  function focusFirstError(submitErrors: LoginFormErrors) {
    if (submitErrors.email) {
      emailRef.current?.focus()
    } else if (submitErrors.password || submitErrors.base) {
      passwordRef.current?.focus()
    }
  }

  useEffect(() => {
    if (prefilledEmail) setField('email', prefilledEmail)
  }, [prefilledEmail])

  return (
    <form
      id="session_form"
      // the handler below preventDefaults, so these never run - they are the floor under a
      // native submit, which bypasses the handler and would otherwise GET the password into the URL
      action={sessionPath}
      method="post"
      className={`tw:font-ubuntu tw:[&_*]:font-ubuntu ${GLASS_FIELDS}`}
      onSubmit={async e => {
        const submitErrors = await handleSubmit(e, { ssoActive: Boolean(sso) })
        if (submitErrors) focusFirstError(submitErrors)
      }}
    >
      {inboxExpired ? (
        <div className="tw:flex tw:flex-col tw:gap-1">
          <h1 className="tw:!font-merriweather tw:!text-[24px] tw:!font-light tw:!leading-normal tw:text-center tw:!text-white">
            Your session has expired.
          </h1>
          <h3 className="tw:!font-ubuntu tw:!text-base tw:!font-bold tw:!leading-normal tw:text-center tw:!text-white">
            Please login to continue.
          </h3>
        </div>
      ) : (
        <h1 className="tw:!font-merriweather tw:!text-[24px] tw:!leading-normal tw:!font-light tw:text-center tw:!text-white tw:w-full tw:max-w-[300px] tw:mx-auto">
          Log in to aytm
        </h1>
      )}

      {/* Safari renders the UUIInput flex row with `align-items: normal` (stretch) and
          styles the `<input>` intrinsic box, knocking the left icon and placeholder
          off-centre. Force centering + reset the input appearance, scoped to this
          form's inputs (no change to the shared UUIInput). */}
      {/* 16px on phones: < 16px triggers iOS focus auto-zoom. Load-bearing for the desktop
          layout served to a narrow window, which has no #content 16px rule like mobile.erb (OQ-CC-7). */}
      <div
        className={`tw:flex tw:flex-col tw:gap-6 tw:pt-6 ${
          // a message below the fields brings its own 16px; without the trim it would sit
          // 24px under the last field and 16px over the buttons, off-centre in its own gap
          errors.base || notice ? 'tw:pb-4' : 'tw:pb-6'
        } tw:[&_[data-uuiinput-left-icon]]:!text-[16px] tw:[&_[data-uuiinput-root]]:items-center tw:[&_[data-uuiinput-main]]:appearance-none tw:max-sm:[&_[data-uuiinput-main]]:text-[16px]`}
      >
        {/* Email */}
        <div className="tw:flex tw:flex-col tw:gap-1 tw:relative">
          <UUIInput
            ref={emailRef}
            type="text"
            name="email"
            uiStyle="outline"
            uiSkin="auth"
            value={form.email}
            onChange={e => setField('email', e.target.value)}
            onBlur={trimOnBlur(value => setField('email', value))}
            placeholder="Email"
            // the placeholder is the only visible name, and it goes away on the first keystroke
            aria-label="Email"
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            autoComplete="email"
            uiInvalid={Boolean(errors.email || errors.base)}
          />
          {errors.email && (
            <span
              id="login-email-error"
              role="alert"
              className="tw:text-xs tw:text-auth-error-text tw:absolute tw:[@media(max-width:560px)]:static tw:bottom-[-18px] tw:left-5"
            >
              {errors.email}
            </span>
          )}
        </div>

        {/* SSO indicator */}
        {sso && <SsoIndicator sso={sso} />}

        {/* Password + remember-me grouped 8px apart per Figma (node 2338:13828); hidden on SSO match */}
        {showPassword && (
          <div className="tw:flex tw:flex-col tw:gap-2">
            {/* Password */}
            <div className="tw:flex tw:flex-col tw:gap-1 tw:relative">
              <UUIInput
                ref={passwordRef}
                type={passwordVisible ? 'text' : 'password'}
                name="password"
                uiStyle="outline"
                uiSkin="auth"
                placeholder="Password"
                aria-label="Password"
                aria-describedby={errors.password ? 'login-password-error' : undefined}
                value={form.password}
                onChange={e => setField('password', e.target.value)}
                autoComplete="current-password"
                uiInvalid={Boolean(errors.password || errors.base)}
                uiRightAddons={
                  // h-5 pins the row to the input's line box so the 24px hit areas below do not
                  // stretch the field taller than every other one; they overflow into the
                  // field's own vertical padding, which is deeper than the 2px they need
                  <span className="tw:flex tw:items-center tw:gap-4 tw:h-5">
                    <a
                      href={restorePasswordPath}
                      target="_top"
                      // 24px min hit area (WCAG 2.5.8): the label itself is only 16px tall
                      className="tw:text-xs tw:text-auth-link tw:hover:text-auth-link-hover tw:hover:underline tw:ml-auto tw:flex tw:items-center tw:min-h-[24px]"
                    >
                      Forgot?
                    </a>

                    {/* no colour transition: WebKit repaints the glyph late and it lands half-recoloured */}
                    <button
                      type="button"
                      onClick={() => setPasswordVisible(v => !v)}
                      aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                      className={`tw:flex tw:items-center tw:justify-center tw:min-w-[24px] tw:min-h-[24px] tw:cursor-pointer ${
                        passwordVisible
                          ? 'tw:text-white tw:hover:text-white'
                          : 'tw:text-auth-icon tw:hover:text-auth-icon-hover'
                      }`}
                    >
                      <i className="icon-aytmfd-view-eye" />
                    </button>
                  </span>
                }
              />
              {/* In-flow (unlike the email error): only 8px separates this group from
                  remember-me, so an absolutely-hung error overlaps the checkbox label */}
              {errors.password && (
                <span id="login-password-error" role="alert" className="tw:text-xs tw:text-auth-error-text tw:pl-5">
                  {errors.password}
                </span>
              )}
            </div>

            {/* Remember me — hidden on SSO match (the SSO short-circuit never consumes remember_me, GAP-020) */}
            <div className="tw:flex tw:flex-col tw:gap-1 tw:pl-4">
              <label className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-white tw:cursor-pointer">
                <UUICheckbox
                  checked={form.rememberMe}
                  onChange={checked => setField('rememberMe', checked ?? false)}
                  uiSize="small"
                />
                Keep me logged in on this device
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Outside the password block on purpose: `showPassword` is false on an SSO match, and
          the server's SSO-unavailable reason lands in `base` — inside, the click looked dead. */}
      {errors.base && (
        <span role="alert" className="tw:text-xs tw:text-center tw:text-auth-error-text tw:pb-4 tw:block">
          {errors.base}
        </span>
      )}

      {/* Arrival notice from the server: a dead password-reset link, or the carried-over
          credentials flash after a reload. Shares the slot with the live error above so it
          reads as part of the form rather than floating over the heading. */}
      {notice && !errors.base && (
        <span role="status" className="tw:text-xs tw:text-center tw:text-auth-warning tw:pb-4 tw:block">
          {notice}
        </span>
      )}

      {/* Cloudflare Turnstile — shown after repeated failed login attempts; hidden on SSO match
          because the SSO short-circuit precedes the captcha gate, so it cannot affect the outcome (GAP-021, SEC-026).
          Below the message on purpose: the widget's opaque plate would otherwise cut the failure
          reason off from the fields it refers to. */}
      {showPassword && captcha.displayCaptcha && captcha.turnstileSiteKey && (
        <div className="tw:flex tw:justify-center tw:items-center tw:pb-4">
          <CloudflareTurnstile
            siteKey={captcha.turnstileSiteKey}
            // not a user edit: keep the message that asked for the captcha on screen
            onToken={setTurnstileToken}
            resetSignal={turnstileResetSignal}
          />
        </div>
      )}

      {/* Actions — Log in (primary) sits left of Sign up. Sign up is shown whenever
          register is on-domain (incl. SSO, matching master's always-visible register);
          hidden only off the main domain (registerPath nil). */}
      <div className="tw:flex tw:items-center tw:justify-center tw:gap-3">
        <UUIButton
          type="submit"
          uiVariant="normal"
          uiStyle="darkBlue"
          className={GLASS_BTN_PRIMARY}
          disabled={loading || (showPassword && captcha.displayCaptcha && !form.turnstileToken)}
          uiLeftIcon={loading ? 'icon-aytmfd-spin tw:animate-spin' : undefined}
        >
          Log in
        </UUIButton>
        {registerPath && (
          <UUIButton href={registerPath} uiVariant="normal" uiStyle="modernGrey" className={GLASS_BTN_GHOST}>
            Sign up
          </UUIButton>
        )}
      </div>
    </form>
  )
}
